// Import necessary types
import Actors from '../../helpers/actors';

// Enum types (matching C# entities)
enum FederationType {
  None = 0
}

enum SectorOfActivities {
  None = 0
}

enum EducationLevel {
  None = 0
}

enum ExperienceType {
  None = 0
}

enum WorkPatternV2 {
  None = 0
}

enum WorkCriterion {
  None = 0
}

enum YearExperience {
  None = 0
}

enum InsuranceExperience {
  None = 0
}

enum ProductionExperience {
  None = 0
}

enum DisasterExperience {
  None = 0
}

enum CommercialExperience {
  None = 0
}

enum BankExperience {
  None = 0
}

enum CreditExperience {
  None = 0
}

enum InvestmentExperience {
  None = 0
}

// Base interface for common properties
interface BaseProfile {
  id: number;
  createdOn: string;
  modifiedOn: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
  picture?: string;
  linkedin?: string;
  facebook?: string;
  twitter?: string;
  instagram?: string;
  userId?: string;
  jobFunctionId?: number;
  partnerId?: number;
  enableConversationsReportEmail: boolean;
  lastActivityDate?: string;
  lastMessageReceivedOn?: string;
}

// CompanyProfile interface
export interface CompanyProfile extends BaseProfile {
  companyNumber?: string;
  companyName?: string;
  companyAddressStreet?: string;
  companyAddressCity?: string;
  companyAddressPostalCode?: string;
  jobTitle?: string;
  contactPersonFirstName?: string;
  contactPersonLastName?: string;
  contactPersonPhoneNumber?: string;
  contactPersonEmail?: string;
  website?: string;
  federationType: FederationType;
  isHighlighted: boolean;
  partnerJoinedOn?: string;
  partnerNewsEmailSentOn?: string;
  sectorOfActivity: SectorOfActivities;
  companyAddressLocationId?: number;
}

// EmployeeProfile interface
export interface EmployeeProfile extends BaseProfile {
  userName?: string;
  birthDate?: string;
  hasDrivingLicense?: boolean;
  hasPcpAggregation?: boolean;
  hasExperienceInDistributionManaging?: boolean;
  isAnonymous: boolean;
  isOpenToWork: boolean;
  curriculumVitae?: string;
  motivationLetter?: string;
  educationLevel: EducationLevel;
  experienceType: ExperienceType;
  workPatternV2: WorkPatternV2;
  workAvailability?: string;
  workCriterion: WorkCriterion;
  workFreelance?: boolean;
  companyExperience: YearExperience;
  brokerOfficeOrAgencyExperience: YearExperience;
  insuranceExperience: InsuranceExperience;
  productionExperience: ProductionExperience;
  disasterExperience: DisasterExperience;
  commercialParticularCustomerExperience: CommercialExperience;
  commercialProfessionalCustomerExperience: CommercialExperience;
  administrativeEmployeeExperience?: boolean;
  agencyExperience: YearExperience;
  headquarterExperience: YearExperience;
  bankExperience: BankExperience;
  creditExperience: CreditExperience;
  investmentExperience: InvestmentExperience;
  dailyBankingExperience?: boolean;
  lastVisitReportDate?: string;
  partnerJoinedOn?: string;
  partnerValidUntil?: string;
  partnerConditionsReadOn?: string;
  lastOpenToWorkInquirySentOn?: string;
  lastInactivityEmailSentOn?: string;
  locationId?: number;
}

// Union type for profile response
export type ProfileLightResponse = CompanyProfile | EmployeeProfile;

// Type guard functions to determine profile type
export function isCompanyProfile(profile: ProfileLightResponse): profile is CompanyProfile {
  return 'companyName' in profile;
}

export function isEmployeeProfile(profile: ProfileLightResponse): profile is EmployeeProfile {
  return 'userName' in profile;
}

// Fetch profile light function with proper typing based on actor
export async function fetchProfileLight<T extends Actors>(
  actor: T
): Promise<T extends Actors.Company ? CompanyProfile : T extends Actors.Employee ? EmployeeProfile : ProfileLightResponse> {
  
  const response = await fetch('/api/profile/me/light', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to fetch profile: ${error}`);
  }

  const data = await response.json();
  return data;
}

export async function checkProfileProgression(userName: string, force: boolean = false) {
  
  if (localStorage.getItem('userName') === null || localStorage.getItem('userName') !== userName) {
    localStorage.removeItem('userComplete');
    sessionStorage.removeItem('canContactCompanies');
  }

  if (
    force ||
    sessionStorage.getItem('canContactCompanies') === null ||
    sessionStorage.getItem('canContactCompanies') === 'undefined' ||
    localStorage.getItem('userComplete') === null ||
    localStorage.getItem('userComplete') === 'undefined'
  ) {
    let response = await fetch('/api/profile/is-complete/me', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    if (response.ok) {
      let data = await response.json();

      localStorage.setItem('userName', data.userName);
      localStorage.setItem('userComplete', data.userComplete);
      sessionStorage.setItem('canContactCompanies', data.canContactCompanies);
    } else {
      // Show in console error message
      let error = await response.text();
      console.log(error);
    }
  }
}